import { Chip, Grid, LinearProgress, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import {
  useArchiveCourseMutation,
  useCreateCourseMutation,
  useDeleteCourseMutation,
  useGetCourseQuery,
  usePublishCourseMutation,
  useUpdateCourseMutation,
} from "../../api/rtk/CourseApi";
import { NewEntityId } from "../../data/constants";
import {
  CourseStateTranslationKey,
  CourseStatus,
  CourseType,
  CourseViewModel,
  RequiredCourse,
} from "../../model/Course";
import { routesPaths } from "../../Routes";
import { GetTranslation } from "../../translations/GetTranslation";
import { navigateBack } from "../../utils/Navigation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import DefaultPublishArchiveButtons from "../common/buttons/DefaultPublishArchiveButtons";
import TranslatedMessage from "../translations/TranslatedMessage";
import { getInitialRequiredFieldsStatuses, initialCourse } from "./CourseConstants";
import CourseFormFields from "./CourseFormFields";
import CourseFormModules from "./CourseFormModules";

const CourseForm: FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const isCreation = courseId === NewEntityId;

  const [updateCourse, { isLoading: isUpdating }] = useUpdateCourseMutation();
  const [createCourse, { isLoading: isCreating }] = useCreateCourseMutation();
  const [publishCourse, { isLoading: isPublishing }] = usePublishCourseMutation();
  const [archiveCourse, { isLoading: isArchiving }] = useArchiveCourseMutation();
  const [deleteCourse, { isLoading: isDeleting, isSuccess: isDeleteSuccess }] = useDeleteCourseMutation();

  const { data: fetchedCourse, isFetching } = useGetCourseQuery(courseId, {
    skip: isCreation || isDeleteSuccess || !courseId,
  });

  const [course, setCourse] = useState<CourseViewModel | undefined>(isCreation ? initialCourse : undefined);
  const [requiredFieldStatus, setRequiredFieldStatus] = useState(getInitialRequiredFieldsStatuses());

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const intl = useIntl();

  useEffect(() => {
    if (!isCreation && fetchedCourse) {
      setCourse(fetchedCourse);
    }
  }, [isCreation, fetchedCourse]);

  const isLoading = isFetching || isUpdating || isCreating || isPublishing || isArchiving || isDeleting;

  if (isLoading) {
    return (
      <div>
        <Typography variant="h5">
          <TranslatedMessage id={"course.header"} />
        </Typography>
        <LinearProgress variant="query" />
      </div>
    );
  }

  if (course === undefined) {
    return null;
  }

  const isReadonly = course.status === CourseStatus.Published || course.status === CourseStatus.Archived;

  const close = () => navigateBack(routesPaths.courses(), history, history.location);

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (isCreation) {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  const handleCreate = async () => {
    const response = await createCourse(course).unwrap();
    if (response.success) {
      history.replace(routesPaths.course(response.data));
    }
  };

  const handleUpdate = async () => {
    const response = await updateCourse(course).unwrap();
    if (response.success) {
      close();
    }
  };

  const handlePublish = async () => {
    if (!validateForm(true)) {
      return;
    }
    const response = await publishCourse(course).unwrap();
    if (response.success) {
      close();
      enqueueSnackbar(<TranslatedMessage id="course.snackbar.successfulPublish" />);
    }
  };

  const handleDelete = async () => {
    const response = await deleteCourse(course.id).unwrap();
    if (response.success) {
      close();
      enqueueSnackbar(<TranslatedMessage id="course.snackbar.successfulDelete" />);
    }
  };

  const handleArchive = async () => {
    const response = await archiveCourse(course).unwrap();
    if (response.success) {
      close();
      enqueueSnackbar(<TranslatedMessage id="course.snackbar.successfulArchive" />);
    }
  };

  const validateForm = (forPublish: boolean = false) => {
    const currentStatus: RequiredCourse = {
      isNameDEEmpty: !course.nameDE,
      isNameENEmpty: !course.nameEN,
      isNameFREmpty: !course.nameFR,
      isNameITEmpty: !course.nameIT,
      isModuleNumbersZero: false,
      isModuleListEmpty: false,
      isTypeEmpty: !course.type || course.type === CourseType.None,
    };

    if (forPublish) {
      currentStatus.isModuleNumbersZero =
        course.numberOfMandatoryModules + course.numberOfProcessModules + course.numberOfSelectionModules <= 0;
      currentStatus.isModuleListEmpty = !course.modules?.length;
    }

    const isInvalid = Object.values(currentStatus).reduce(
      (prev, curr) => (typeof curr === "boolean" ? prev || curr : prev),
      false
    );

    setRequiredFieldStatus(currentStatus);
    return !isInvalid;
  };

  return (
    <>
      <Typography variant="h5">
        <TranslatedMessage id={"course.header"} />
      </Typography>

      <DefaultButtons
        additionalButtons={
          <>
            <Chip label={GetTranslation(intl, CourseStateTranslationKey[course.status])} />
            <DefaultPublishArchiveButtons
              isArchiveButtonVisible={course.status === CourseStatus.Published}
              isPublishButtonVisible={course.status === CourseStatus.Draft && !isCreation}
              onPublish={handlePublish}
              onArchive={handleArchive}
            />
          </>
        }
        onClose={() => history.goBack()}
        onDelete={handleDelete}
        onSave={handleSave}
        isDeleteButtonVisible={course.status === CourseStatus.Draft && !isCreation}
        isSaveButtonVisible={course.status === CourseStatus.Draft}
      />

      <Grid container={true} direction="column" spacing={4}>
        <Grid item={true}>
          <CourseFormFields
            course={course}
            isCreation={isCreation}
            handleFieldChange={(change: Partial<CourseViewModel>) => setCourse({ ...course, ...change })}
            requiredFieldStatus={requiredFieldStatus}
            isReadOnly={isReadonly}
          />
        </Grid>

        <Grid item={true}>
          {!isCreation && (
            <CourseFormModules
              courseModules={course.modules}
              handleAddModules={(modules) => setCourse({ ...course, modules: [...course.modules, ...modules] })}
              handleRemoveModule={(module) => {
                setCourse({
                  ...course,
                  modules: course.modules.filter((m) => m.id !== module.id),
                  modulesRelevantForGrade: course.modulesRelevantForGrade?.filter((m) => m !== module.id),
                });
              }}
              isReadOnly={isReadonly}
              requiredFieldStatus={requiredFieldStatus}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default CourseForm;
